import { keepPreviousData } from '@tanstack/react-query';

import { useDataQuery } from '@/hooks/shared/useDataQuery';
import { useInAppNotificationsAvailable } from '@/hooks/inAppNotification/useInAppNotificationsAvailable';
import { fetchInAppNotificationsPage } from '@/queries/inAppNotification';
import { QUERY_KEYS } from '@/resources/dataKeys';
import type {
  InAppNotificationsPageData,
  InAppNotificationsPageParams,
} from '@/types/inAppNotification';

/**
 * Prefix shared by every cached page, so the mark-read mutations can reach all
 * of them at once without knowing which filters are open.
 */
export const IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY = [QUERY_KEYS.inAppNotificationsPage];

/**
 * One numbered page of the all-notifications view.
 *
 * The previous page stays on screen while the next one loads, so changing page
 * or filter does not flash the skeleton. The unread count is not read from here:
 * the badge's own cache entry is the single source of truth for it.
 */
export const useInAppNotificationsPage = (params: InAppNotificationsPageParams) => {
  const isAvailable = useInAppNotificationsAvailable();

  const { data, ...rest } = useDataQuery<InAppNotificationsPageData>(
    [...IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY, params],
    () => fetchInAppNotificationsPage(params),
    {
      enabled: isAvailable,
      placeholderData: keepPreviousData,
    },
  );

  return {
    notifications: data?.notifications ?? [],
    pagination: data?.pagination,
    ...rest,
  };
};
